import { useEffect, useRef, useState } from "react";
import { Camera, CheckCircle2, Loader2, RotateCcw, ShieldCheck } from "lucide-react";
import axios from "axios";
import { renderScript } from "../lib/script";
import { AvatarIntro, AvatarDone } from "../components/verify/Avatar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const QS = new URLSearchParams(window.location.search);
const SESSION = QS.get("session") || "";
const wq = (() => { const p = new URLSearchParams(); ["session", "merchant", "w"].forEach((k) => QS.get(k) && p.set(k, QS.get(k))); const t = p.toString(); return t ? `?${t}` : ""; })();
const EMBEDDED = window.self !== window.top;
const notifyParent = (origins, msg) => { if (EMBEDDED) (origins || []).forEach((o) => window.parent.postMessage({ source: "aegis", ...msg }, o)); };
const SECONDS = 10;
const secondsFor = (script, avatar) => Math.min(avatar ? 20 : 30, Math.max(avatar ? 15 : SECONDS, Math.ceil(script.split(/\s+/).length / 2.2) + 4));
let START = null;
export const isLight = (hex) => { const h = (hex || "#000000").slice(1); const [r, g, b] = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16)); return 0.299 * r + 0.587 * g + 0.114 * b > 150; };
export const themeVars = (t = {}) => ({ background: t.background || "#0B0D10", "--v-card": t.card || "#14171C", "--v-accent": t.accent || "#FFFFFF", "--v-accent-text": isLight(t.accent || "#FFFFFF") ? "#0B0D10" : "#FFFFFF" });
const TERMS = (cfg) => `/terms?merchant=${encodeURIComponent(cfg.slug || "")}`;
document.title = "Account verification";
const pickMime = () => ["video/mp4;codecs=avc1,mp4a.40.2", "video/mp4", "video/webm;codecs=vp8,opus", "video/webm"].find((m) => window.MediaRecorder?.isTypeSupported?.(m)) || "";
const report = (sid, kind, detail = "") => sid && axios.post(`${API}/verify/${sid}/client-event${wq}`, { kind, detail: String(detail).slice(0, 300) }).catch(() => {});
const sha256 = async (t) => [...new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(t)))].map((b) => b.toString(16).padStart(2, "0")).join("");

const Shell = ({ merchant, terms, theme, children }) => (
  <div className={`min-h-screen text-white flex flex-col items-center px-4 py-10 ${isLight(theme?.background) ? "verify-light" : ""}`} style={themeVars(theme)} data-testid="verify-page">
    {theme?.logo_url ? <img src={theme.logo_url} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }} className="h-10 max-w-[200px] object-contain" data-testid="verify-logo" /> : null}
    <p className="font-semibold tracking-wide text-white/80 mt-2" data-testid="verify-merchant">{merchant}</p>
    <div className="w-full max-w-lg mt-6 border border-white/10 rounded-2xl p-6 sm:p-8 shadow-2xl" style={{ background: "var(--v-card)" }}>{children}</div>
    <p className="mt-6 text-[11px] text-white/35 flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Secure account verification · encrypted, private storage{terms && <> · <a href={terms} target="_blank" rel="noreferrer" className="underline hover:text-white/70" data-testid="verify-terms-footer-link">Terms &amp; Privacy</a></>}</p>
  </div>
);

const Btn = ({ children, kind = "primary", ...p }) => (
  <button {...p} style={kind === "primary" ? { background: "var(--v-accent)", color: "var(--v-accent-text)" } : undefined}
    className={`w-full py-3 rounded-xl font-semibold transition-[filter,opacity] disabled:opacity-40 ${kind === "primary" ? "hover:brightness-95" : "bg-white/5 text-white hover:bg-white/10 border border-white/10"}`}>{children}</button>
);

export default function VerifyAccount() {
  const q = new URLSearchParams(window.location.search);
  const [cfg, setCfg] = useState(null);
  const C = (k, d) => (cfg?.copy?.[k] || d);
  const [next, setNext] = useState(null);
  const [sid, setSid] = useState(null);
  const [step, setStep] = useState("consent");
  const [agree, setAgree] = useState(false);
  const [name, setName] = useState(q.get("name") || "");
  const [amount, setAmount] = useState(0);
  const [code, setCode] = useState("");
  const askEmail = !SESSION && !q.get("email") && !q.get("payment_id") && !q.get("payment") && !q.get("membership_id") && !q.get("membership");
  const [email, setEmail] = useState("");
  const [left, setLeft] = useState(SECONDS);
  const [clip, setClip] = useState(null);
  const [err, setErr] = useState("");
  const live = useRef(null), stream = useRef(null), rec = useRef(null), started = useRef(0), tick = useRef(null);
  const finish = () => { clearInterval(tick.current); rec.current?.state !== "inactive" && rec.current?.stop(); };

  useEffect(() => {
    axios.get(`${API}/verify/config${wq}`).then((r) => { setCfg(r.data); document.title = `${r.data.merchant_name} · ${r.data.copy?.page_title || "Account verification"}`; }).catch(() => setErr("This page is unavailable right now. Please refresh."));
    START ||= axios.post(`${API}/verify/start${wq}`, { session: SESSION, referrer: window.self !== window.top ? document.referrer : "", email: q.get("email") || "", name: q.get("name") || "", payment_id: q.get("payment_id") || q.get("payment") || "",
      membership_id: q.get("membership_id") || q.get("membership") || "", user_id: q.get("user_id") || "" });
    START.then((r) => { setSid(r.data.id); setAmount(r.data.amount || 0); if (r.data.name) setName((n) => n || r.data.name); })
      .catch((e) => setErr(e.response?.data?.detail || "Couldn't start verification. Please refresh."));
    return () => stream.current?.getTracks().forEach((t) => t.stop());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const go = (url) => { if (!EMBEDDED && url) window.location.href = url; };
  const skip = async () => {
    const r = await axios.post(`${API}/verify/${sid}/skip${wq}`).catch(() => null);
    notifyParent(cfg.embed_origins, { type: "aegis.verification.skipped", verification_id: sid, redirect_url: cfg.redirect_url });
    if (EMBEDDED) setStep("skipped"); else go(r?.data?.redirect_url || cfg.redirect_url);
  };

  const openCamera = async () => {
    setErr("");
    try {
      stream.current = await navigator.mediaDevices.getUserMedia({ video: { width: 640, height: 480, facingMode: "user" }, audio: true });
      setStep("ready");
      setTimeout(() => { if (live.current) live.current.srcObject = stream.current; }, 50);
      report(sid, "camera_opened");
    } catch (e) {
      report(sid, "camera_denied", `${e?.name || ""} ${e?.message || ""} · ${navigator.userAgent}`);
      setErr("Camera and microphone are needed to activate your access. Allow them for this page in your browser settings, then tap Continue again.");
    }
  };

  const record = () => {
    const mime = pickMime();
    const chunks = [];
    let r;
    try { r = new MediaRecorder(stream.current, mime ? { mimeType: mime, videoBitsPerSecond: 1_000_000 } : undefined); }
    catch (e) { report(sid, "recorder_error", `${e?.message || e} · mime=${mime} · ${navigator.userAgent}`); setErr("Recording isn't supported in this browser. Please open this page in Safari or Chrome."); return; }
    r.ondataavailable = (e) => e.data.size && chunks.push(e.data);
    r.onstop = () => {
      const type = (r.mimeType || mime || "video/webm").split(";")[0];
      const blob = new Blob(chunks, { type });
      setClip({ blob, url: URL.createObjectURL(blob), duration: (Date.now() - started.current) / 1000 });
      setStep("review");
    };
    rec.current = r;
    started.current = Date.now();
    r.start(250);
    setStep("recording");
    let n = secondsFor(script, avatar);
    setLeft(n);
    tick.current = setInterval(() => { n -= 1; setLeft(n); if (n <= 0) { clearInterval(tick.current); r.state !== "inactive" && r.stop(); } }, 1000);
  };

  const retake = () => { URL.revokeObjectURL(clip.url); setClip(null); setStep("ready"); setTimeout(() => { if (live.current) live.current.srcObject = stream.current; }, 50); };

  const submit = async () => {
    setStep("uploading");
    try {
      const f = new FormData();
      f.append("video", clip.blob, `verification.${clip.blob.type.includes("mp4") ? "mp4" : "webm"}`);
      f.append("consent_accepted", "true");
      f.append("consent_hash", await sha256(cfg.consent_text));
      f.append("duration_s", String(clip.duration.toFixed(1)));
      f.append("name", name);
      if (askEmail) f.append("email", email.trim());
      const r = await axios.post(`${API}/verify/${sid}/upload${wq}`, f, { timeout: 150000 });
      if (r.data.retry) { retake(); setErr(r.data.message); return; }
      stream.current?.getTracks().forEach((t) => t.stop());
      setStep("done");
      notifyParent(cfg.embed_origins, { type: "aegis.verification.completed", verification_id: sid, linked: r.data.linked, redirect_url: r.data.redirect_url });
      setNext(r.data.redirect_url);
      setCode(r.data.reward_code || "");
      if (cfg.experience !== "avatar") setTimeout(() => go(r.data.redirect_url), 2200);
    } catch (e) {
      report(sid, "upload_failed", `${e.response?.status || "network"} ${e.response?.data?.detail || e.message} · ${clip.blob.type} ${clip.blob.size}B · ${navigator.userAgent}`);
      setErr(e.response?.data?.detail || "Upload failed. Please try again.");
      setStep("review");
    }
  };

  const avatar = cfg?.experience === "avatar";
  const script = renderScript(cfg, name, amount);
  if (!cfg) return <Shell merchant="" theme={{}}><p className="text-sm text-white/60" data-testid="verify-loading">{err || "Loading…"}</p></Shell>;
  return (
    <Shell merchant={cfg.merchant_name} terms={TERMS(cfg)} theme={cfg.theme}>
      {step === "consent" && (
        <div data-testid="verify-step-consent">
          {cfg.experience === "avatar" ? <AvatarIntro cfg={cfg} /> : <>
          <h1 className="text-2xl font-bold">{cfg.headline}</h1>
          <p className="text-sm text-white/65 mt-2">{cfg.intro}</p></>}
          <label className="block mt-5 text-xs text-white/55">{C("name_label", "Your full name")}
            <input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full bg-black/40 border border-white/15 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-white/50" data-testid="verify-name-input" />
          </label>
          {askEmail && <label className="block mt-3 text-xs text-white/55">{C("email_label", "Email you used at checkout")}
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 w-full bg-black/40 border border-white/15 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-white/50" data-testid="verify-email-input" />
          </label>}
          <div className="mt-5 max-h-44 overflow-y-auto text-[12px] leading-relaxed text-white/60 bg-black/30 border border-white/10 rounded-lg p-3" data-testid="verify-consent-text">{cfg.consent_text}</div>
          <label className="flex items-start gap-3 mt-4 text-sm cursor-pointer">
            <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-1 w-4 h-4" style={{ accentColor: "var(--v-accent)" }} data-testid="verify-consent-checkbox" />
            <span>I agree to the <a href={TERMS(cfg)} target="_blank" rel="noreferrer" className="underline" data-testid="verify-terms-link">Terms &amp; Privacy</a> and consent to this recording, its transcript, and their use as described there.</span>
          </label>
          {err && <p className="text-sm text-red-400 mt-3" data-testid="verify-error">{err}</p>}
          <div className="mt-5 space-y-2">
            {EMBEDDED && <p className="text-[11px] text-white/40">If the camera doesn't open, ask the store to allow camera and microphone on this frame.</p>}
            <Btn onClick={openCamera} disabled={!agree || name.trim().length < 2 || !sid || (askEmail && !/^\S+@\S+\.\S+$/.test(email.trim()))} data-testid="verify-open-camera-btn"><Camera className="inline w-4 h-4 mr-2" />{C("camera_button", "Continue to camera")}</Btn>
            {cfg.allow_skip && <button onClick={skip} className="w-full text-xs text-white/40 hover:text-white/70 py-2" data-testid="verify-skip-btn">Skip for now</button>}
          </div>
        </div>
      )}
      {(step === "ready" || step === "recording") && (
        <div data-testid="verify-step-record">
          <div className="relative rounded-xl overflow-hidden bg-black aspect-[4/3]">
            <video ref={live} autoPlay muted playsInline className="w-full h-full object-cover -scale-x-100" data-testid="verify-live-video" />
            {step === "recording" && <span className="absolute top-3 left-3 flex items-center gap-2 bg-black/70 rounded-full px-3 py-1 text-sm" data-testid="verify-countdown"><span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />REC · {left}s</span>}
          </div>
          {err && <p className="text-sm text-red-400 mt-3 text-center" data-testid="verify-retry-msg">{err}</p>}
          <p className="text-center mt-4 text-[11px] uppercase tracking-widest text-white/45">{C("record_title", "Read this out loud, word for word")}</p>
          <p className="text-center mt-1 text-lg font-semibold leading-snug" data-testid="verify-script">"{script}"</p>
          <div className="mt-4">{step === "recording"
            ? <Btn onClick={finish} disabled={left > secondsFor(script, avatar) * 0.4} data-testid="verify-finish-btn">{left > secondsFor(script, avatar) * 0.4 ? "Keep reading…" : C("finish_button", "I'm done, stop recording")}</Btn>
            : <Btn onClick={record} data-testid="verify-record-btn">{C("record_button", "Start {seconds}-second recording").replace("{seconds}", secondsFor(script, avatar))}</Btn>}</div>
          {cfg.allow_skip && step === "ready" && <button onClick={skip} className="w-full text-xs text-white/40 hover:text-white/70 py-2 mt-1" data-testid="verify-skip-camera-btn">Skip for now</button>}
        </div>
      )}
      {step === "review" && clip && (
        <div data-testid="verify-step-review">
          <video src={clip.url} controls playsInline className="w-full rounded-xl bg-black aspect-[4/3]" data-testid="verify-review-video" />
          {err && <p className="text-sm text-red-400 mt-3" data-testid="verify-error">{err}</p>}
          <div className="grid grid-cols-2 gap-2 mt-4">
            <Btn kind="ghost" onClick={retake} data-testid="verify-retake-btn"><RotateCcw className="inline w-4 h-4 mr-2" />Retake</Btn>
            <Btn onClick={submit} data-testid="verify-submit-btn">{C("submit_button", "Verify & continue")}</Btn>
          </div>
        </div>
      )}
      {step === "uploading" && <p className="flex items-center justify-center gap-2 py-10 text-white/70" data-testid="verify-uploading"><Loader2 className="w-5 h-5 animate-spin" /> {C("uploading", "Checking your recording…")}</p>}
      {step === "done" && cfg.experience === "avatar" && (
        <div data-testid="verify-step-done"><AvatarDone cfg={cfg} code={code} seed={sid} name={name} onContinue={() => go(next || cfg.redirect_url)} /></div>
      )}
      {step === "done" && cfg.experience !== "avatar" && (
        <div className="text-center py-8" data-testid="verify-step-done">
          <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-400" />
          <p className="text-xl font-bold mt-3" data-testid="verify-done-title">{C("done_title", "You're verified")}</p>
          <p className="text-sm text-white/60 mt-1">{C("done_body", EMBEDDED ? "You can continue now." : "Taking you to the next step…")}</p>
        </div>
      )}
      {step === "skipped" && <p className="text-center py-8 text-white/70" data-testid="verify-step-skipped">No problem. You can continue now.</p>}
    </Shell>
  );
}
